import React, { useMemo, useRef, useState } from 'react';
import AndroidRoundedIcon from '@mui/icons-material/AndroidRounded';
import AppleIcon from '@mui/icons-material/Apple';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CableRoundedIcon from '@mui/icons-material/CableRounded';
import SatelliteAltRoundedIcon from '@mui/icons-material/SatelliteAltRounded';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import { APP_PLATFORMS } from './UserPanelHomeShowcases';
import { SETUP_APPS } from './userPanelSetupData';
import './userPanelSetup.css';

const AVAILABLE_OFFICIAL_APPS = APP_PLATFORMS
  .filter((app) => app.setupAvailable !== false)
  .flatMap((app) => (app.id === 'android'
    ? [app, { ...app, id: 'android-tv' }, { ...app, id: 'fire-tv' }]
    : [app]))
  .concat({ id: 'titanos', name: 'TitanOS TV' });

const SETUP_TABLE_TABS = [
  {
    id: 'official',
    label: 'Resmî Uygulamalar',
    icon: AppsRoundedIcon,
  },
  {
    id: 'tools',
    label: 'Server / M3U',
    icon: CableRoundedIcon,
  },
  {
    id: 'smart-tv',
    label: 'Smart TV',
    icon: TvRoundedIcon,
  },
  {
    id: 'android',
    label: 'Android',
    icon: AndroidRoundedIcon,
  },
  {
    id: 'ios',
    label: 'iPhone / macOS',
    icon: AppleIcon,
  },
  {
    id: 'computer',
    label: 'Bilgisayar',
    icon: AppsRoundedIcon,
  },
  {
    id: 'satellite',
    label: 'Uydu Alıcı',
    icon: SatelliteAltRoundedIcon,
  },
];

const LEGACY_SETUP_META = {
  'server-info': { loading: 'Elle Giriş', stars: 5 },
  m3u: { loading: 'Elle Giriş', stars: 2 },
  'smart-iptv': { loading: 'Manuel', stars: 3 },
  'ss-iptv': { loading: 'Manuel', stars: 2 },
  'asx-iptv': { loading: 'Manuel', stars: 3 },
  smartone: { loading: 'Manuel', stars: 2 },
  televizo: { loading: 'Elle Giriş', stars: 3 },
  'vu-iptv': { loading: 'Elle Giriş', stars: 5 },
  uhf: { loading: 'Elle Giriş', stars: 5 },
  'smarters-lite-ios': { loading: 'Elle Giriş', stars: 3 },
  'cloud-stream': { loading: 'Elle Giriş', stars: 3 },
  'iptvx-ios': { loading: 'Elle Giriş', stars: 4 },
  'xtream-plugin': { loading: 'Elle Giriş', stars: 5 },
  nextstar: { loading: 'Manuel', stars: 1 },
};

const OFFICIAL_SETUP_DETAILS = {
  android: { name: 'Tivigo Android', description: 'Tivigo’nun resmî Android uygulaması' },
  'android-tv': { name: 'Tivigo Android TV', description: 'Tivigo’nun resmî Android TV uygulaması' },
  'fire-tv': { name: 'Tivigo Fire TV', description: 'Tivigo’nun resmî Amazon Fire Stick TV uygulaması' },
  windows: { name: 'Tivigo Windows', description: 'Tivigo’nun resmî Windows uygulaması' },
  ios: { name: 'Tivigo iOS', description: 'Tivigo’nun resmî iOS uygulaması' },
  lg: { name: 'Tivigo LG webOS', description: 'Tivigo’nun resmî LG webOS uygulaması' },
  titanos: { name: 'TitanOS TV', description: 'Tivigo’nun resmî TitanOS TV uygulaması.' },
  macos: { name: 'Tivigo macOS', description: 'Tivigo’nun resmî macOS uygulaması' },
};

const OFFICIAL_SETUP_DETAIL_ROUTES = {
  android: 'tivigo',
  'android-tv': 'tivigo-tv',
  'fire-tv': 'tivigo-fire-tv',
  windows: 'tivigo-pc',
  ios: 'tivigo-ios',
  lg: 'tivigo-lg-webos',
  titanos: 'tivigo-titanos',
  macos: 'tivigo-macos',
};

const ALTERNATIVE_SETUP_DETAIL_ROUTES = {
  'smarters-lite-ios': 'smarters-lite',
};

const createOfficialSetupRow = (app, overrides = {}) => {
  const details = OFFICIAL_SETUP_DETAILS[app.id] || {};
  return {
    id: overrides.id || `official-${app.id}`,
    official: true,
    app,
    detailId: overrides.detailId || OFFICIAL_SETUP_DETAIL_ROUTES[app.id] || app.id,
    logo: '/assets/images/tivigo-app-icon.png',
    name: overrides.name || details.name || app.name,
    description: overrides.description || details.description || app.points[0],
    loading: 'Otomatik',
    stars: 5,
  };
};

const createAlternativeSetupRow = (app) => ({
  id: app.id,
  official: false,
  app,
  detailId: ALTERNATIVE_SETUP_DETAIL_ROUTES[app.id] || app.id,
  logo: app.logo,
  name: app.name,
  description: app.description,
  loading: LEGACY_SETUP_META[app.id]?.loading || 'Elle Giriş',
  stars: LEGACY_SETUP_META[app.id]?.stars || 3,
});

const getSetupTableRows = (tabId) => {
  if (tabId === 'official') return AVAILABLE_OFFICIAL_APPS.map((app) => createOfficialSetupRow(app));

  if (tabId === 'smart-tv') {
    const androidApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'android-tv');
    const fireTvApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'fire-tv');
    const lgApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'lg');
    const titanOsApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'titanos');
    return [
      ...SETUP_APPS.filter((app) => app.category === 'smart-tv').map(createAlternativeSetupRow),
      ...(androidApp ? [createOfficialSetupRow(androidApp)] : []),
      ...(fireTvApp ? [createOfficialSetupRow(fireTvApp)] : []),
      ...(lgApp ? [createOfficialSetupRow(lgApp)] : []),
      ...(titanOsApp ? [createOfficialSetupRow(titanOsApp)] : []),
    ];
  }

  if (tabId === 'android') {
    const androidApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'android');
    return [
      ...(androidApp ? [createOfficialSetupRow(androidApp)] : []),
      ...SETUP_APPS.filter((app) => app.category === 'android').map(createAlternativeSetupRow),
    ];
  }

  if (tabId === 'ios') {
    const iosApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'ios');
    const macosApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'macos');
    const featuredAppIds = ['vu-iptv', 'uhf'];
    return [
      ...SETUP_APPS.filter((app) => featuredAppIds.includes(app.id)).map(createAlternativeSetupRow),
      ...(iosApp ? [createOfficialSetupRow(iosApp)] : []),
      ...(macosApp ? [createOfficialSetupRow(macosApp)] : []),
      ...SETUP_APPS
        .filter((app) => app.category === 'ios' && !featuredAppIds.includes(app.id))
        .map(createAlternativeSetupRow),
    ];
  }

  if (tabId === 'computer') {
    const windowsApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'windows');
    const macosApp = AVAILABLE_OFFICIAL_APPS.find((app) => app.id === 'macos');
    return [
      ...(windowsApp ? [createOfficialSetupRow(windowsApp)] : []),
      ...SETUP_APPS.filter((app) => app.id === 'uhf').map(createAlternativeSetupRow),
      ...(macosApp ? [createOfficialSetupRow(macosApp)] : []),
    ];
  }

  const category = tabId === 'tools' ? 'tools' : tabId;
  return SETUP_APPS.filter((app) => app.category === category).map(createAlternativeSetupRow);
};

function UserPanelSetup({ targetUsername = null, onOpenSetupDetail }) {
  const [activeTab, setActiveTab] = useState('official');
  const tabListRef = useRef(null);
  const activeTabMeta = SETUP_TABLE_TABS.find((tab) => tab.id === activeTab) || SETUP_TABLE_TABS[0];
  const alternativeCount = SETUP_APPS.filter((app) => app.category !== 'tools').length;

  const tableRows = useMemo(() => getSetupTableRows(activeTab), [activeTab]);

  const renderOfficialAction = (row) => {
    const content = <><span>Kurulum Yap</span><ArrowForwardRoundedIcon /></>;
    return (
      <button
        className="tvup-setup-table-primary is-automatic"
        type="button"
        onClick={() => onOpenSetupDetail?.(row.detailId)}
      >
        {content}
      </button>
    );
  };

  const handleTabKeyDown = (event) => {
    const supportedKeys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!supportedKeys.includes(event.key)) return;
    event.preventDefault();
    const currentIndex = Math.max(0, SETUP_TABLE_TABS.findIndex((tab) => tab.id === activeTab));
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? SETUP_TABLE_TABS.length - 1
        : event.key === 'ArrowRight'
          ? (currentIndex + 1) % SETUP_TABLE_TABS.length
          : (currentIndex - 1 + SETUP_TABLE_TABS.length) % SETUP_TABLE_TABS.length;
    setActiveTab(SETUP_TABLE_TABS[nextIndex].id);
    window.requestAnimationFrame(() => tabListRef.current?.querySelectorAll('[role="tab"]')?.[nextIndex]?.focus());
  };

  return (
    <div className="tvup-setup-page">
      <section className="tvup-setup-table-intro" aria-labelledby="tvup-setup-title">
        <div>
          {/* Bayi bir alt kullanıcı için kurulum yaparken hangi hesap olduğu başlıkta görünür. */}
          <h1 id="tvup-setup-title">{targetUsername ? `${targetUsername} - Kurulum Merkezi` : 'Kurulum Merkezi'}</h1>
          <p>Cihazınıza uygun uygulamayı seçin; indirme bağlantısına, giriş bilgilerine ve kurulum adımlarına tek ekrandan ulaşın.</p>
        </div>
        <div className="tvup-setup-table-summary" aria-label="Kurulum seçenekleri özeti">
          <span><small>Resmî uygulama</small><b>{AVAILABLE_OFFICIAL_APPS.length}</b></span>
          <span><small>Alternatif uygulama</small><b>{alternativeCount}</b></span>
          <span><small>Bağlantı aracı</small><b>{SETUP_APPS.filter((app) => app.category === 'tools').length}</b></span>
        </div>
      </section>

      <div className="tvup-setup-table-directory">
        <div ref={tabListRef} className="tvup-setup-table-tabs" role="tablist" aria-label="Kurulum kategorileri">
          {SETUP_TABLE_TABS.map((tab) => {
            const TabIcon = tab.icon;
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls="tvup-setup-table-content"
                tabIndex={selected ? 0 : -1}
                className={selected ? 'is-active' : ''}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={handleTabKeyDown}
              >
                <TabIcon />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        <section className="tvup-setup-table-panel" aria-label="Kurulum seçenekleri">
          <div
            className="tvup-setup-table"
            id="tvup-setup-table-content"
            role="table"
            aria-label={`${activeTabMeta.label} kurulum listesi`}
            aria-rowcount={tableRows.length}
          >
            <div className="tvup-setup-table-head" role="row">
              <span role="columnheader">Logo</span>
              <span role="columnheader">Uygulama Adı</span>
              <span role="columnheader">Açıklama</span>
              <span role="columnheader">Yükleme</span>
              <span role="columnheader">Tavsiye</span>
              <span role="columnheader">İşlem Yap</span>
            </div>

            <div className="tvup-setup-table-body">
              {tableRows.map((row) => {
                const loadingClass = row.loading === 'Otomatik'
                  ? 'is-automatic'
                  : row.loading === 'Manuel'
                    ? 'is-manual'
                    : 'is-manual-entry';
                const actionLabel = row.loading === 'Elle Giriş'
                  ? 'Detaylar'
                  : row.loading === 'Manuel'
                    ? 'Nasıl Yapılır'
                    : 'Kurulum Yap';
                return (
                  <article key={row.id} className={`tvup-setup-table-row${row.official ? ' is-official' : ''}`} role="row">
                    <span className="tvup-setup-table-logo-cell" role="cell">
                      <span className="tvup-setup-table-logo"><img src={row.logo} alt="" /></span>
                    </span>

                    <span className="tvup-setup-table-name" role="cell" data-label="Uygulama Adı">{row.name}</span>
                    <span className="tvup-setup-table-description" role="cell" data-label="Açıklama">{row.description}</span>
                    <div className="tvup-setup-table-meta" role="presentation">
                      <span className={`tvup-setup-table-loading ${loadingClass}`} role="cell" data-label="Yükleme">
                        <b>{row.loading}</b>
                      </span>
                      <span className="tvup-setup-table-rating" role="cell" data-label="Tavsiye" aria-label={`${row.stars} üzerinden 5 tavsiye puanı`}>
                        <span className="tvup-setup-table-rating-stars" aria-hidden="true">
                          {[0, 1, 2, 3, 4].map((index) => <i key={index} className={index < row.stars ? 'is-filled' : ''}>★</i>)}
                        </span>
                      </span>
                    </div>

                    <div className="tvup-setup-table-actions" role="cell">
                      {row.official ? renderOfficialAction(row) : (
                        <button className={`tvup-setup-table-primary ${loadingClass}`} type="button" onClick={() => onOpenSetupDetail?.(row.detailId)}>
                          <span>{actionLabel}</span><ArrowForwardRoundedIcon />
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

        </section>
      </div>
    </div>
  );
}

export default UserPanelSetup;
